import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import React, { useMemo, useRef, useState } from 'react';
import AppProvider from 'components/AppProvider';
import FormProps, { AudienceType, FormAnswer, QuestionOptionsResponsesByQnID } from '../../../../models/FormProps';
import { UserMetadata } from '../../../../form_elements/BaseElement';
import TopBar from '../../../../TopBar';
import { isNotEmpty } from '../../../../utils/equality';
import Content from '../../../forms/show/Content';
import { userSort } from '../../../../utils/sort';

interface Props {
  homeUrl: string;
  form: FormProps;
  userMetadata: UserMetadata;
  answers: Array<FormAnswer>;
  questionOptionsResponsesByQnID?: QuestionOptionsResponsesByQnID;
  previewUser: {
    id: string;
    name: string;
  }
  audienceType: AudienceType;
  audience: Array<{
    id: string;
    name: string;
    level_code?: number;
    clazz_code?: string;
    clazz_name?: string;
    index_number?: number;
  }>;
  previewUrl: string;
  previewIframeSource: string | false;
  formId: string;
}

export default function Page(props: Props) {
  const { previewUser, audienceType, audience, previewUrl, previewIframeSource, formId } = props;
  const [selectedUserID, setSelectedUserID] = useState(previewUser.id);
  const useV2Preview = !!previewIframeSource;
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const iframeSrc = useMemo(() => {
    if (!previewIframeSource) return '';
    return `${previewIframeSource}/${formId}/preview`;
  }, [previewIframeSource, formId]);

  const sendMessageToIframe = (message: { type: string; [key: string]: unknown }) => {
    if (iframeRef.current?.contentWindow) {
      const targetOrigin = new URL(previewIframeSource as string).origin;
      iframeRef.current.contentWindow.postMessage(message, targetOrigin);
    }
  };

  return (
    <AppProvider>
      <TopBar {...props} username={previewUser.name}>
        {isNotEmpty(audience) && renderUserSelector()}
      </TopBar>

      {useV2Preview ? (
        <iframe
          ref={iframeRef}
          title="form-preview"
          className="preview-iframe"
          src={iframeSrc}
          onLoad={() => { sendMessageToIframe({ type: 'parent-ready' }); }}
        />
      ) : (
        <Content {...props} disableSubmit />
      )}
    </AppProvider>
  );

  function renderUserSelector() {
    const sortedAudience = userSort(audienceType, audience);

    return (
      <TextField
        select
        size="small"
        className="user-selector"
        label="Preview as user"
        InputLabelProps={{ shrink: true }}
        variant="outlined"
        value={selectedUserID}
        onChange={onChangePreviewUser}
      >
        <MenuItem key="placeholder" value="">&lt;no user selected&gt;</MenuItem>

        {sortedAudience.map(user => {
          let { name } = user;

          if ((audienceType === AudienceType.STUDENT || audienceType === AudienceType.PARENT) && isNotEmpty(user.clazz_name)) {
            name = `${user.clazz_name} #${user.index_number} ${user.name}`;
          }

          return <MenuItem key={user.id} value={user.id}>{name}</MenuItem>;
        })}
      </TextField>
    );
  }

  function onChangePreviewUser(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) {
    const userID = event.target.value;

    setSelectedUserID(userID);

    if (useV2Preview) {
      const user = audience.find(u => u.id === userID);
      sendMessageToIframe({ type: 'preview-user-change', user: user ?? null });
    } else {
      location.href = previewUrl + '?user=' + userID;
    }
  }
}
